import { createContext, useContext, useEffect, useState } from 'react'
import { useGetQuery } from 'hooks'
import { useSocket } from './socketProvider'
import { useStateValue } from './stateProvider'

const FeatureFlagsContext = createContext({ flags: {}, isLoading: true })

export const useFeatureFlags = () => useContext(FeatureFlagsContext)

export const useFeatureFlag = (key, fallback = { enabled: false, variant: null, value: {} }) => {
  const { flags, isLoading } = useFeatureFlags()
  if (key === undefined) return { ...flags, isLoading }
  return { ...(flags[key] || fallback), isLoading }
}

export const FeatureFlagsProvider = ({ children }) => {
  const [{ session: { user } }] = useStateValue()
  const socket = useSocket()
  const [flags, setFlags] = useState({})
  const { data, isFetching } = useGetQuery({ url: '/feature_flags' })

  useEffect(() => {
    if (data?.feature_flags) setFlags(data.feature_flags)
  }, [data])

  useEffect(() => {
    if (!user?.id || !socket) return

    const onOverride = ({ key, enabled, variant, value, overridden }) => {
      setFlags((prev) => ({ ...prev, [key]: { enabled, variant, value, overridden } }))
    }

    const onDefaultChanged = ({ key, enabled, variant, value }) => {
      setFlags((prev) => {
        const current = prev[key]
        if (current?.overridden) return prev
        return { ...prev, [key]: { enabled, variant, value, overridden: false } }
      })
    }

    const userChannel = `/users/${user.id}/feature_flags`
    socket.on(userChannel, onOverride)
    socket.on('feature_flags:default_changed', onDefaultChanged)
    return () => {
      socket.off(userChannel, onOverride)
      socket.off('feature_flags:default_changed', onDefaultChanged)
    }
  }, [socket, user?.id])

  return (
    <FeatureFlagsContext.Provider value={{ flags, isLoading: isFetching }}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}
